import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AuthProvider } from '../features/auth/AuthProvider';
import ProtectedRoute from '../features/auth/ProtectedRoute';
import { NotificationProvider } from '../contexts/NotificationContext';
import { DecisionQueueProvider } from '../contexts/DecisionQueueContext';
import { AppSnackbarProvider } from '../shared/utils/snackbar';
import theme from './theme';
import AppLayout from './AppLayout';
import LoginPage from '../features/auth/LoginPage';
import DashboardRoute from '../features/dashboard/DashboardRoute';
import ClientOverview from '../features/clients/ClientOverview';
import ClientDetails from '../features/clients/ClientDetails';
import Reports from '../features/reports/Reports';
import TeamPerformance from '../features/team/TeamPerformance';
import { StaffPerformancePage } from '../features/staffPerformance';
import AdminPanel from '../features/admin/AdminPanel';
import IgnitionOverview from '../features/ignition/IgnitionOverview';
import { FoundationOverview } from '../features/foundation/FoundationOverview';
import PrePostNatalOverview from '../features/prePostNatal/PrePostNatalOverview';
import NotepadTasks from '../features/notepad';
import { SuccessOverviewOptimized, SuccessNominations } from '../features/success';
import { Feedback } from '../features/feedback';
import { Guides } from '../features/guides';
import { Drive } from '../features/drive';
import Notices from '../features/notices/Notices';
import { LeaveManager } from '../features/leave';
import NotFound from '../shared/components/NotFound';
import FeatureRoute from './FeatureRoute';
import LactateThresholdPage from '../features/lactateThreshold/LactateThresholdPage';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppSnackbarProvider>
      <AuthProvider>
        <NotificationProvider>
        <DecisionQueueProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/clients" element={<ClientOverview />} />
            <Route path="/clients/:clientId" element={<ClientDetails />} />
            <Route path="/ignition" element={<IgnitionOverview />} />
            <Route path="/foundation" element={<FoundationOverview />} />
            <Route path="/pre-post-natal" element={<PrePostNatalOverview />} />
            <Route path="/success" element={<SuccessOverviewOptimized />} />
            <Route
              path="/success-nominations"
              element={
                <ProtectedRoute requiredRole="MANAGER">
                  <SuccessNominations />
                </ProtectedRoute>
              }
            />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/notices" element={<Notices />} />
            <Route path="/reports" element={<Reports />} />
            <Route
              path="/lactate-threshold"
              element={
                <FeatureRoute feature="lactate_testing">
                  <LactateThresholdPage />
                </FeatureRoute>
              }
            />
            <Route path="/notepad" element={<NotepadTasks />} />
            {/* Team Performance stays ADMIN-only. Staff Performance is open to
                every signed-in role (coaches get their own page), matching the
                sidebar entries in AppLayout. */}
            <Route
              path="/team-performance"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <TeamPerformance />
                </ProtectedRoute>
              }
            />
            <Route path="/staff-performance" element={<StaffPerformancePage />} />
            <Route path="/staff-performance/:staffId" element={<StaffPerformancePage />} />
            <Route
              path="/leave-manager"
              element={
                <ProtectedRoute>
                  <LeaveManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drive"
              element={
                <ProtectedRoute>
                  <Drive />
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={<AdminPanel />} />
          </Route>
          
          <Route path="*" element={<NotFound />} />
        </Routes>
        </DecisionQueueProvider>
        </NotificationProvider>
      </AuthProvider>
      </AppSnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
